import { OUTPOSTS, VISION } from '@sd/content';
import { hasAgreement, type Agreement } from './diplomacy';
import { PIRATES, capitalHex, type GameState } from './game';
import { liveCloudAt } from './exploration';
import type { FleetState } from './fleet';
import { hexDistance, hexKey, hexSpiral, type Hex } from './hex';
import type { OutpostState } from './outposts';
import type { StarSystem } from './system';

/**
 * Fog of war (#33): what a player sees, worked out from the state alone so
 * every client and the server agree. The map itself (star, planets, dust
 * clouds, outposts) is common knowledge; fleets are not.
 */
export interface VisionInput {
  readonly system: StarSystem;
  readonly fleets: readonly FleetState[];
  readonly outposts: readonly OutpostState[];
  readonly agreements: readonly Agreement[];
  /** Each player's capital hex. */
  readonly capitals: ReadonlyMap<string, Hex>;
  /** Finds left in each cloud: a dispersed cloud hides nothing (#103). */
  readonly findsLeft?: Readonly<Record<string, number>>;
}

/** Players whose eyes `viewer` shares: itself and its allies. */
export function sharedEyes(input: VisionInput, viewer: string): string[] {
  return [...input.capitals.keys()].filter(
    (id) => id === viewer || hasAgreement(input.agreements, viewer, id, 'alliance'),
  );
}

/** Where `viewer` (with its allies) has eyes: centres and how far each sees. */
export function eyesOf(input: VisionInput, viewer: string): { hex: Hex; radius: number }[] {
  const eyes = new Set(sharedEyes(input, viewer));
  const out: { hex: Hex; radius: number }[] = [];
  for (const id of eyes) {
    const capital = input.capitals.get(id);
    if (capital) out.push({ hex: capital, radius: VISION.capital });
  }
  for (const f of input.fleets) {
    if (!eyes.has(f.owner)) continue;
    const explorer = f.ships.some((s) => s.cls === 'explorer');
    out.push({ hex: f.hex, radius: explorer ? VISION.explorer : VISION.fleet });
  }
  for (const o of input.outposts) {
    if (eyes.has(o.owner)) {
      out.push({ hex: o.hex, radius: OUTPOSTS[o.kind].zoneRadius + VISION.outpostBeyondZone });
    }
  }
  return out;
}

/** Hexes `viewer` sees, as hex keys. */
export function visibleHexes(input: VisionInput, viewer: string): Set<string> {
  const seen = new Set<string>();
  for (const { hex, radius } of eyesOf(input, viewer)) {
    for (const h of hexSpiral(hex, radius)) seen.add(hexKey(h));
  }
  return seen;
}

/**
 * Whether `viewer` sees a hex: the same answer as `visibleHexes`, by
 * distance to its eyes rather than by listing every hex — cheaper when only
 * a few hexes are asked about, as bots do every second (#50).
 */
export function canSee(input: VisionInput, viewer: string): (h: Hex) => boolean {
  const eyes = eyesOf(input, viewer);
  return (h) => eyes.some((e) => hexDistance(e.hex, h) <= e.radius);
}

/**
 * Fleets `viewer` sees: its own; pirate bands wherever they are (#91: a
 * band, once woken, is a lair on everyone's map until it is beaten); anyone
 * else's in a visible hex, unless it hides in a dust cloud where neither the
 * viewer nor an ally has a fleet. A dispersed cloud hides nothing (#103).
 */
export function seenFleets(
  input: VisionInput,
  viewer: string,
  visible: ReadonlySet<string> | ((h: Hex) => boolean) = canSee(input, viewer),
): FleetState[] {
  const sees = typeof visible === 'function' ? visible : (h: Hex) => visible.has(hexKey(h));
  const eyes = new Set(sharedEyes(input, viewer));
  const clouds = new Set(
    input.fleets
      .filter((f) => eyes.has(f.owner))
      .map((f) => liveCloudAt(input.system, input.findsLeft, f.hex)?.id)
      .filter((id): id is string => id !== undefined),
  );
  return input.fleets.filter((f) => {
    if (eyes.has(f.owner) || f.owner === PIRATES) return true;
    if (!sees(f.hex)) return false;
    const cloud = liveCloudAt(input.system, input.findsLeft, f.hex);
    return !cloud || clouds.has(cloud.id);
  });
}

/** The vision input of a whole match state. */
export function visionInput(state: GameState): VisionInput {
  return {
    system: state.system,
    fleets: state.fleets,
    outposts: state.outposts,
    agreements: state.agreements,
    capitals: new Map(state.players.map((p) => [p.id, capitalHex(state, p.id)])),
    findsLeft: state.exploration.findsLeft,
  };
}
